import { getLocalDateOnlyFromISO } from '@src/utility/Utils';
import { api } from '.';
import {
  IAppointmentResponse,
  ISchedule,
  IScheduleAddOn,
  IScheduleCustomer,
  IScheduleLocation,
  IScheduleRecipient,
  IScheduleRoom,
  IScheduleTherapist,
  IScheduleTimeSlot,
  IScheduleTreatment,
} from '../calendar/types';
import { TimeZone } from '../business/locations/types';

export interface IAppointmentRequest {
  businessId: string;
  customer: IScheduleCustomer;
  location: IScheduleLocation;
  room: IScheduleRoom;
  recipients: IScheduleRecipient[];
  therapists: IScheduleTherapist[];
  treatments: IScheduleTreatment[];
  addOns: IScheduleAddOn[];
  timeSlot: IScheduleTimeSlot;
  groupSize: number;
}

export interface IRescheduleAppointmentRequest {
  id: string;
  room: IScheduleRoom;
  recipients: IScheduleRecipient[];
  therapists: IScheduleTherapist[];
  treatments: IScheduleTreatment[];
  addOns: IScheduleAddOn[];
  timeSlot: IScheduleTimeSlot;
  groupSize: number;
}

export interface IExtendAppointmentRequest {
  id: string;
  recipients: IScheduleRecipient[];
  treatments: IScheduleTreatment[];
  durationInMinutes: number;
  bufferTimeInMinutes: number;
}

export enum CreditType {
  GiftCard = 'GiftCard',
}

export type Credit = {
  type: CreditType;
  paymentToken: string;
  isGlobal?: boolean;
};

export interface IUpdateAppointmentRequest {
  id: string;
  room: IScheduleRoom;
  recipients: IScheduleRecipient[];
  therapists: IScheduleTherapist[];
  treatments: IScheduleTreatment[];
  addOns: IScheduleAddOn[];
  groupSize: number;
}

export interface IUpdateAddOnsRequest {
  id: string;
  recipients: IScheduleRecipient[];
  addOns: IScheduleAddOn[];
}

export interface IUpdateAppointmentNotesRequest {
  id: string;
  notes: string;
}

export enum UpdateAppointmentStatusType {
  Scheduled = 'Scheduled',
  NoShow = 'NoShow',
  Arrived = 'Arrived',
  CheckedIn = 'CheckedIn',
  CheckedOut = 'CheckedOut',
}

export interface IUpdateAppointmentStatusRequest {
  id: string;
  status: UpdateAppointmentStatusType;
}

export interface IBlockedTimeRequest {
  businessId: string;
  locationId: string;
  therapist: IScheduleTherapist;
  timeSlot: IScheduleTimeSlot;
  internalNotes: string;
}

export interface IUpdateBlockedTimeRequest {
  id: string;
  timeSlot: IScheduleTimeSlot;
}

export interface IUpdateBlockedTimeNotesRequest {
  id: string;
  internalNotes: string;
}

interface IGetScheduleRequest {
  locationId: string;
  startDate: string;
  endDate: string;
}

export interface ICancelAppointmentRequest {
  id: string;
  applyCancellationPolicy: boolean;
  refundToGiftCard: boolean;
  giftCardCanBeUsedAtOtherLocations: boolean;
}

// Define a service using a base URL and expected endpoints
export const scheduleApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getScheduleByLocation: builder.query<ISchedule, IGetScheduleRequest>({
      query: (request) => {
        if (!request.locationId) throw new Error('locationId is required.');
        return {
          url: `/schedule/location/${request.locationId}?Starts=${request.startDate}&Ends=${request.endDate}`,
          method: 'get',
        };
      },
      providesTags: ['schedule'],
      transformResponse: async (response: ISchedule) => {
        response.blockedTimes.forEach((blockedTime) => {
          const localDateOnly = getLocalDateOnlyFromISO(
            blockedTime.timeSlot.starts,
            TimeZone[blockedTime.timeSlot.timeZone as keyof typeof TimeZone]
          );
          blockedTime.localDateOnly = localDateOnly;
        });
        response.appointments.forEach((appointment) => {
          const localDateOnly = getLocalDateOnlyFromISO(
            appointment.timeSlot.starts,
            TimeZone[appointment.timeSlot.timeZone as keyof typeof TimeZone]
          );
          appointment.localDateOnly = localDateOnly;
        });
        return response;
      },
    }),
    getTherapistScheduleByLocation: builder.query<
      ISchedule,
      IGetScheduleRequest
    >({
      query: (request) => {
        if (!request.locationId) throw new Error('locationId is required.');
        return {
          url: `/schedule/location/${request.locationId}/therapist/me?Starts=${request.startDate}&Ends=${request.endDate}`,
          method: 'get',
        };
      },
      providesTags: ['therapist-schedule'],
      transformResponse: (response: ISchedule) => {
        response.blockedTimes.forEach((blockedTime) => {
          const localDateOnly = getLocalDateOnlyFromISO(
            blockedTime.timeSlot.starts,
            TimeZone[blockedTime.timeSlot.timeZone as keyof typeof TimeZone]
          );
          blockedTime.localDateOnly = localDateOnly;
        });
        response.appointments.forEach((appointment) => {
          const localDateOnly = getLocalDateOnlyFromISO(
            appointment.timeSlot.starts,
            TimeZone[appointment.timeSlot.timeZone as keyof typeof TimeZone]
          );
          appointment.localDateOnly = localDateOnly;
        });
        return response;
      },
    }),
    addAppointment: builder.mutation<string, IAppointmentRequest>({
      query: (data) => {
        return {
          url: `/schedule/appointment`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['schedule', 'availability', 'gift-card'],
      transformResponse: (_response, meta) => {
        const appointmentId =
          meta?.locationHeader?.replace('appointment/', '') ?? '';
        return appointmentId;
      },
    }),
    rescheduleAppointment: builder.mutation<
      void,
      IRescheduleAppointmentRequest
    >({
      query: ({ id, ...data }) => {
        const requestData = {
          ...data,
          treatments: data.treatments.map((t) => ({
            serviceId: t.serviceId,
            name: t.name,
            type: t.type,
            price: t.price,
            category: t.category,
          })),
        };
        return {
          url: `/schedule/appointment/${id}/reschedule`,
          method: 'PUT',
          data: requestData,
        };
      },
      invalidatesTags: [
        'schedule',
        'availability',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-appointments',
        'gift-card',
      ],
    }),
    extendAppointment: builder.mutation<void, IExtendAppointmentRequest>({
      query: ({ id, ...data }) => {
        const requestData = { ...data };
        return {
          url: `/schedule/appointment/${id}/extend`,
          method: 'PUT',
          data: requestData,
        };
      },
      invalidatesTags: [
        'schedule',
        'availability',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-appointments',
        'gift-card',
      ],
    }),
    updateAppointment: builder.mutation<void, IUpdateAppointmentRequest>({
      query: ({ id, ...data }) => {
        return {
          url: `/schedule/appointment/${id}`,
          method: 'PUT',
          data,
        };
      },
      invalidatesTags: [
        'schedule',
        'availability',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-appointments',
        'gift-card',
      ],
    }),
    updateAddOns: builder.mutation<void, IUpdateAddOnsRequest>({
      query: ({ id, ...data }) => {
        return {
          url: `/schedule/appointment/${id}/add-ons`,
          method: 'PUT',
          data,
        };
      },
      invalidatesTags: [
        'availability',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-appointments',
        'gift-card',
      ],
    }),
    updateAppointmentStatus: builder.mutation<
      void,
      IUpdateAppointmentStatusRequest
    >({
      query: ({ id, ...data }) => {
        return {
          url: `/schedule/appointment/${id}/status`,
          method: 'PUT',
          data,
        };
      },
      invalidatesTags: [
        'schedule',
        'availability',
        'booking-order',
        'customer-orders',
        'location-orders',
        'customer-appointments',
      ],
    }),
    updateAppointmentNotes: builder.mutation<
      void,
      IUpdateAppointmentNotesRequest
    >({
      query: ({ id, ...data }) => {
        return {
          url: `/schedule/appointment/${id}/notes`,
          method: 'PUT',
          data,
        };
      },
      invalidatesTags: ['schedule'],
    }),
    removeAppointmentBufferTime: builder.mutation<void, string>({
      query: (appointmentId) => {
        return {
          url: `/schedule/appointment/${appointmentId}/buffer-time`,
          method: 'PUT',
          data: {},
        };
      },
      invalidatesTags: ['schedule', 'availability'],
    }),
    cancelAppointment: builder.mutation<void, ICancelAppointmentRequest>({
      query: (data) => {
        return {
          url: `/schedule/appointment/${data.id}/cancel`,
          method: 'PUT',
          data: {
            applyCancellationPolicy: data.applyCancellationPolicy,
            refundToGiftCard: data.refundToGiftCard,
            giftCardCanBeUsedAtOtherLocations:
              data.giftCardCanBeUsedAtOtherLocations,
          },
        };
      },
      invalidatesTags: ['schedule', 'availability', 'gift-card'],
    }),
    addBlockedTime: builder.mutation<void, IBlockedTimeRequest>({
      query: (data) => {
        return {
          url: `/schedule/block`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['schedule', 'availability'],
    }),
    updateBlockedTime: builder.mutation<void, IUpdateBlockedTimeRequest>({
      query: ({ id, ...data }) => {
        return {
          url: `/schedule/block/${id}`,
          method: 'PUT',
          data,
        };
      },
      invalidatesTags: ['schedule', 'availability'],
    }),
    updateBlockedTimeNotes: builder.mutation<
      void,
      IUpdateBlockedTimeNotesRequest
    >({
      query: ({ id, ...data }) => {
        return {
          url: `/schedule/block/${id}/notes`,
          method: 'PUT',
          data,
        };
      },
      invalidatesTags: ['schedule'],
    }),
    removeBlockedTime: builder.mutation<void, string>({
      query: (blockedTimeId) => {
        return {
          url: `/schedule/block/${blockedTimeId}`,
          method: 'DELETE',
          data: null,
        };
      },
      invalidatesTags: ['schedule', 'availability'],
    }),
    getCustomerAppointments: builder.query<IAppointmentResponse[], string>({
      query: (customerId) => {
        if (!customerId) throw new Error('locationId is required.');
        return {
          url: `/schedule/appointments/customer/${customerId}`,
          method: 'get',
        };
      },
      providesTags: ['customer-appointments'],
      transformResponse: (response: IAppointmentResponse[]) => {
        return response;
      },
    }),
    getCustomerAppointmentHistory: builder.query<
      IAppointmentResponse[],
      string
    >({
      query: (customerId) => {
        if (!customerId) throw new Error('locationId is required.');
        return {
          url: `/schedule/appointments/customer/${customerId}/history`,
          method: 'get',
        };
      },
      providesTags: ['customer-appointment-history'],
      transformResponse: (response: IAppointmentResponse[]) => {
        return response;
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetScheduleByLocationQuery,
  useLazyGetScheduleByLocationQuery,
  useGetTherapistScheduleByLocationQuery,
  useAddAppointmentMutation,
  useUpdateAddOnsMutation,
  useRescheduleAppointmentMutation,
  useExtendAppointmentMutation,
  useUpdateAppointmentStatusMutation,
  useUpdateAppointmentNotesMutation,
  useRemoveAppointmentBufferTimeMutation,
  useUpdateAppointmentMutation,
  useCancelAppointmentMutation,
  useAddBlockedTimeMutation,
  useUpdateBlockedTimeMutation,
  useUpdateBlockedTimeNotesMutation,
  useRemoveBlockedTimeMutation,
  useGetCustomerAppointmentsQuery,
  useGetCustomerAppointmentHistoryQuery,
} = scheduleApi;
